import { useState } from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, X } from "lucide-react";
import { PROJECTS, CATEGORIES, wixImg } from "../data/projects";
import { useSEO } from "../lib/seo";

const ProjectPage = () => {
  const { category, slug } = useParams();
  const navigate = useNavigate();
  const [lightbox, setLightbox] = useState(null);

  const cat = CATEGORIES.find((c) => c.key === category);
  const projects = PROJECTS[category] || [];
  const idx = projects.findIndex((p) => p.slug === slug);
  const projectName = idx !== -1 ? projects[idx].name : "Projekt";
  useSEO(
    `${projectName} – ${cat ? cat.label : "Referencia"} | New Idea Architect`,
    `${projectName} – megvalósult építészeti projektünk képekkel. Tervezés, engedélyezés, megvalósítás egy kézből.`,
    `/portfolio/${category}/${slug}`
  );
  if (!cat || idx === -1) return <Navigate to="/munkaink" replace />;

  const project = projects[idx];
  const prev = projects[(idx - 1 + projects.length) % projects.length];
  const next = projects[(idx + 1) % projects.length];

  const goContact = () => navigate("/#kapcsolat");

  return (
    <main className="flex flex-col gap-2.5 pb-2.5 pt-2.5">
      {/* Fekete fejléc box a világos háttéren */}
      <section className="px-2.5">
        <div className="rounded-2xl bg-[var(--card-dark)] p-8 sm:p-12">
          <Link
            to={`/portfolio/${category}`}
            className="inline-flex items-center gap-2 text-white/80 text-sm font-bold hover:text-[var(--accent)] transition-colors mb-6"
          >
            <ArrowLeft size={16} /> {cat.label.toUpperCase()}
          </Link>
          <h1 className="text-white heading-font font-bold text-5xl sm:text-6xl lg:text-7xl tracking-tight">
            {project.name}
          </h1>
        </div>
      </section>

      {/* Galéria – masonry, természetes képarány, nincs levágás */}
      <section className="px-2.5">
        <div className="columns-1 sm:columns-2 gap-2.5">
          {project.images.map((img, i) => (
            <button
              key={img}
              onClick={() => setLightbox(i)}
              className="block w-full mb-2.5 break-inside-avoid rounded-xl overflow-hidden cursor-zoom-in bg-[var(--tile-ph)] group relative"
            >
              <img
                src={wixImg(img, 900)}
                alt={`${project.name} – ${i + 1}. kép`}
                loading={i < 2 ? "eager" : "lazy"}
                decoding="async"
                className="w-full h-auto block"
              />
              <span className="absolute inset-0 rounded-xl ring-0 group-hover:ring-4 ring-[var(--accent)] ring-inset transition-shadow duration-200 pointer-events-none" />
            </button>
          ))}
        </div>
      </section>

      {/* Előző / Következő */}
      <section className="px-2.5">
        <div className="rounded-2xl bg-[var(--card-dark)] px-6 sm:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <Link
            to={`/portfolio/${category}/${prev.slug}`}
            className="inline-flex items-center gap-2 text-white font-bold text-sm hover:text-[var(--accent)] transition-colors"
          >
            <ChevronLeft size={18} /> Előző
          </Link>
          <button
            onClick={goContact}
            className="px-8 py-4 bg-[var(--accent)] text-black text-sm font-bold rounded-md hover:bg-white transition-colors duration-200"
          >
            SZERETNÉK HASONLÓ PROJEKTET
          </button>
          <Link
            to={`/portfolio/${category}/${next.slug}`}
            className="inline-flex items-center gap-2 text-white font-bold text-sm hover:text-[var(--accent)] transition-colors"
          >
            Következő <ChevronRight size={18} />
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-5 right-5 text-white hover:text-[var(--accent)] transition-colors"
            onClick={() => setLightbox(null)}
            aria-label="Bezárás"
          >
            <X size={30} />
          </button>
          <button
            className="absolute left-3 sm:left-8 text-white hover:text-[var(--accent)] transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setLightbox((lightbox - 1 + project.images.length) % project.images.length);
            }}
            aria-label="Előző kép"
          >
            <ChevronLeft size={40} />
          </button>
          <img
            src={wixImg(project.images[lightbox], 1600)}
            alt={`${project.name} – nagy kép`}
            className="max-h-[88vh] max-w-[85vw] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="absolute right-3 sm:right-8 text-white hover:text-[var(--accent)] transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setLightbox((lightbox + 1) % project.images.length);
            }}
            aria-label="Következő kép"
          >
            <ChevronRight size={40} />
          </button>
        </div>
      )}
    </main>
  );
};

export default ProjectPage;
